import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/da08s7g3256a3o8korbg";

interface FormStatus {
  type: "idle" | "success" | "error";
  message: string;
}

export default function ContactSection() {
  const [status, setStatus] = useState<FormStatus>({ type: "idle", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("company_alt") as string) || "";
    if (honeypot.trim() !== "") {
      form.reset();
      setStatus({ type: "success", message: "感谢你的留言，我们会尽快与你联系！" });
      return;
    }

    formData.delete("company_alt");
    const name = (formData.get("name") as string)?.trim() || "";
    const email = (formData.get("email") as string)?.trim() || "";
    const message = (formData.get("message") as string)?.trim() || "";
    if (!name || !email || !message) {
      setStatus({ type: "error", message: "请填写必填项（称呼、邮箱、留言内容）。" });
      return;
    }

    setSubmitting(true);
    setStatus({ type: "idle", message: "" });
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam/i.test(responseText) || /spam/i.test(serverMsg || "");
      const ok = res.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        form.reset();
        setStatus({ type: "success", message: "感谢你的留言，我们会尽快与你联系！" });
      } else {
        setStatus({ type: "error", message: (serverMsg && !isSpam ? serverMsg : "提交失败，请稍后重试。") as string });
      }
    } catch {
      setStatus({ type: "error", message: "网络异常，请稍后重试。" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 md:grid-cols-2 md:px-6">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
            <i className="ri-chat-3-line"></i> 联系我们
          </span>
          <h2 className="mt-4 font-heading text-2xl text-foreground-900 md:text-3xl">与我们取得联系</h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
            无论你是想投稿推荐新番、反馈建议，还是寻求合作，都欢迎随时联系我们。动漫网会认真阅读每一条留言，期待与你的交流。
          </p>

          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className="ri-mail-line text-xl"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-900">邮箱</p>
                <p className="mt-1 text-sm text-foreground-600">service@dongmanwang.com</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                <i className="ri-phone-line text-xl"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-900">电话</p>
                <p className="mt-1 text-sm text-foreground-600">400-888-6666（工作日 9:00 - 18:00）</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className="ri-map-pin-line text-xl"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-900">地址</p>
                <p className="mt-1 text-sm text-foreground-600">中国 · 上海市动漫产业园区 A 座 301 室</p>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form="da08s7g3256a3o8korbg"
          className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-700">称呼 <span className="text-primary-600">*</span></label>
              <input
                id="contact-name"
                name="name"
                type="text"
                placeholder="你的称呼"
                className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-800 outline-none transition focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-700">邮箱 <span className="text-primary-600">*</span></label>
              <input
                id="contact-email"
                name="email"
                type="email"
                placeholder="you@example.com"
                className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-800 outline-none transition focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
              />
            </div>
          </div>
          <div className="mt-4">
            <label htmlFor="contact-subject" className="mb-1.5 block text-sm font-medium text-foreground-700">留言主题</label>
            <select
              id="contact-subject"
              name="subject"
              className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-800 outline-none transition focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
            >
              <option value="">请选择主题</option>
              <option value="投稿推荐">投稿推荐</option>
              <option value="建议反馈">建议反馈</option>
              <option value="商务合作">商务合作</option>
              <option value="其他">其他</option>
            </select>
          </div>
          <div className="mt-4">
            <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-700">留言内容 <span className="text-primary-600">*</span></label>
            <textarea
              id="contact-message"
              name="message"
              rows={5}
              maxLength={500}
              placeholder="请输入你的留言内容（不超过 500 字）"
              className="w-full resize-none rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-800 outline-none transition focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
            ></textarea>
          </div>

          <div className="honeypot-field" aria-hidden="true">
            <label htmlFor="company-name">公司名称</label>
            <input id="company-name" type="text" name="company_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
          </div>

          {status.message && (
            <div className={`mt-4 flex items-center gap-2 rounded-md px-3 py-2.5 text-sm ${status.type === "success" ? "bg-accent-100 text-accent-700" : "bg-secondary-100 text-secondary-700"}`}>
              <i className={status.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"}></i>
              {status.message}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-5 inline-flex w-full items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {submitting ? "提交中..." : "提交留言"}
            {!submitting && <i className="ri-send-plane-line ml-2"></i>}
          </button>
        </form>
      </div>
    </section>
  );
}